import { supabase } from "@/integrations/supabase/client";
import { ensureFreshSession } from "@/lib/authSession";
import { Sentry } from "@/lib/sentry";

// Botão 2 — "Já tenho meu avatar" (task 96bc7d74). O site de consumidor da
// MetaPerson entrega ARQUIVO no aparelho, não URL; então:
//   1. sobe o .glb (e o .png opcional) para avatars/staging/{uid}/{uuid}.*
//      com o JWT do próprio usuário (policy: INSERT/SELECT só no prefixo dele);
//   2. chama metaperson-import com { stagingPath, posterStagingPath?, gender };
//   3. a function valida o rig server-side, injeta o idle, move para
//      generated/ e apaga o staging.
// A validação REAL é do servidor (rigRules.ts) — aqui só pré-checks de UX.

export const UPLOAD_MAX_GLB_BYTES = 60 * 1024 * 1024; // espelha MAX_GLB_BYTES da function
export const UPLOAD_MAX_PNG_BYTES = 10 * 1024 * 1024; // espelha MAX_POSTER_BYTES

// Códigos que a function devolve para o caminho de upload. Cada um tem
// mensagem própria nos 6 locales — mensagem genérica não serve: a pessoa
// precisa saber que basta re-exportar marcando uma caixa.
export const UPLOAD_ERROR_CODES = [
  "invalid_glb",
  "upload_too_large",
  "upload_not_found",
  "invalid_staging_path",
  "skeleton_missing",
  "avaturn_visemes",
  "visemes_missing",
  "visemes_incomplete",
] as const;

export type UploadErrorCode = (typeof UPLOAD_ERROR_CODES)[number] | "unknown";

export class MetaPersonUploadError extends Error {
  code: UploadErrorCode;
  constructor(code: UploadErrorCode, message?: string) {
    super(message || "metaperson-upload: " + code);
    this.name = "MetaPersonUploadError";
    this.code = code;
  }
}

function isKnownCode(value: unknown): value is (typeof UPLOAD_ERROR_CODES)[number] {
  return (
    typeof value === "string" &&
    (UPLOAD_ERROR_CODES as readonly string[]).includes(value)
  );
}

// Lê o body JSON de um FunctionsHttpError (mesmo padrão tolerante do
// readInvokeErrorBody em useNutritionPhotoAnalyze / quotaError.ts: context é
// normalmente a Response crua, mas tolera objeto já parseado).
async function extractErrorCode(err: unknown): Promise<UploadErrorCode> {
  try {
    const ctx = (err as { context?: unknown } | null | undefined)?.context;
    if (!ctx) return "unknown";
    const maybeRes = ctx as { json?: () => Promise<unknown>; clone?: () => Response };
    let body: unknown = null;
    if (typeof maybeRes.json === "function") {
      const src = typeof maybeRes.clone === "function" ? maybeRes.clone() : maybeRes;
      body = await (src as { json: () => Promise<unknown> }).json();
    } else if (typeof ctx === "object") {
      body = ctx;
    }
    const code = (body as Record<string, unknown> | null)?.error;
    return isKnownCode(code) ? code : "unknown";
  } catch {
    return "unknown";
  }
}

export interface OwnAvatarUploadInput {
  glbFile: File;
  posterFile?: File | null;
  gender: "male" | "female";
}

// Sobe o export do usuário e importa. Lança MetaPersonUploadError com o
// código da recusa (ou "unknown") — o chamador traduz.
export async function importOwnAvatar({
  glbFile,
  posterFile,
  gender,
}: OwnAvatarUploadInput): Promise<Record<string, unknown>> {
  // Pré-checks de UX (autoridade é o servidor).
  if (glbFile.size === 0) throw new MetaPersonUploadError("invalid_glb", "empty file");
  if (glbFile.size > UPLOAD_MAX_GLB_BYTES) {
    throw new MetaPersonUploadError("upload_too_large");
  }

  const sessionOk = await ensureFreshSession();
  if (!sessionOk) throw new MetaPersonUploadError("unknown", "no authenticated session");
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new MetaPersonUploadError("unknown", "no user");

  const stamp = crypto.randomUUID();
  const stagingPath = "staging/" + user.id + "/" + stamp + ".glb";

  const { error: upErr } = await supabase.storage
    .from("avatars")
    .upload(stagingPath, glbFile, {
      contentType: "model/gltf-binary",
      upsert: false,
    });
  if (upErr) {
    console.error("[metapersonUpload] staging upload failed:", upErr);
    throw new MetaPersonUploadError("unknown", upErr.message);
  }

  // Poster é opcional e NUNCA bloqueia: falha aqui só derruba o poster.
  let posterStagingPath: string | undefined;
  if (posterFile && posterFile.size > 0 && posterFile.size <= UPLOAD_MAX_PNG_BYTES) {
    const candidate = "staging/" + user.id + "/" + stamp + ".png";
    const { error: posterErr } = await supabase.storage
      .from("avatars")
      .upload(candidate, posterFile, { contentType: "image/png", upsert: false });
    if (posterErr) {
      console.warn("[metapersonUpload] poster staging upload failed:", posterErr);
    } else {
      posterStagingPath = candidate;
    }
  }

  const { data, error } = await supabase.functions.invoke("metaperson-import", {
    body: { stagingPath, posterStagingPath, gender },
  });
  if (error) {
    const code = await extractErrorCode(error);
    console.error("[metapersonUpload] import failed:", code, error);
    if (code === "unknown") {
      try {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { feature: "metaperson_upload_failed" },
        });
      } catch { /* Sentry not initialized in dev */ }
    }
    throw new MetaPersonUploadError(code);
  }

  const avatar = data && (data as Record<string, unknown>).avatar;
  if (!avatar) throw new MetaPersonUploadError("unknown", "missing avatar in response");
  return avatar as Record<string, unknown>;
}
